// Hide the hash target before the browser's load-time fragment scroll.
// Glide still changes page height after that; restore the id and pin
// scroll after mount once layout has settled. Keep the hash in the URL
// so refresh still lands on the same section.
const pendingHash = window.location.hash;
let pendingHashEl = null;
let pendingHashId = "";
if (pendingHash) {
  history.scrollRestoration = "manual";
  pendingHashId = pendingHash.slice(1);
  try {
    pendingHashId = decodeURIComponent(pendingHashId);
  } catch (_err) {
    // keep raw id
  }
  pendingHashEl = document.getElementById(pendingHashId);
  pendingHashEl?.removeAttribute("id");
}

const restorePendingHashEl = () => {
  if (pendingHashEl && pendingHashId && !pendingHashEl.id) {
    pendingHashEl.id = pendingHashId;
  }
};

document.addEventListener("DOMContentLoaded", () => {
  // DOM READY
  const hashId = (hash) => {
    const raw = (hash || "").replace(/^#/, "");
    if (!raw) return "";
    try {
      return decodeURIComponent(raw);
    } catch (_err) {
      return raw;
    }
  };

  // Someone who has asked their OS to reduce motion still wants to arrive at
  // the target — they just don't want the journey animated.
  const prefersReducedMotion = () =>
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const motionSafe = (behavior) =>
    prefersReducedMotion() ? "auto" : behavior;

  const scrollToHashTarget = (hash, behavior = "auto") => {
    const id = hashId(hash);
    if (!id) return false;

    const target = document.getElementById(id);
    if (!target || target.classList.contains("glide__slide--clone")) {
      return false;
    }

    const marginTop = parseFloat(getComputedStyle(target).scrollMarginTop) || 0;
    const top = target.getBoundingClientRect().top + window.scrollY - marginTop;
    window.scrollTo({ top, behavior: motionSafe(behavior) });
    return true;
  };

  // Glide/images keep shrinking the page after load; keep correcting until
  // the target's document position is stable.
  const pinHashTarget = (hash) => {
    const id = hashId(hash);
    if (!id) return;

    let lastDocTop = null;
    let stableFrames = 0;
    const started = performance.now();

    const tick = () => {
      const target = document.getElementById(id);
      if (target && !target.classList.contains("glide__slide--clone")) {
        const docTop = target.getBoundingClientRect().top + window.scrollY;
        scrollToHashTarget(hash, "auto");

        if (lastDocTop !== null && Math.abs(docTop - lastDocTop) < 1) {
          stableFrames += 1;
        } else {
          stableFrames = 0;
        }
        lastDocTop = docTop;
      }

      if (stableFrames >= 8 || performance.now() - started > 2500) {
        return;
      }

      requestAnimationFrame(tick);
    };

    requestAnimationFrame(tick);
  };

  // 00 - AGE GATE
  // The gate is rendered open on every page and hidden by the head script once
  // it sees the cookie, so everything here is an upgrade on top of markup that
  // already works on its own. The No button is a plain link and needs nothing.
  const ageDialog = document.getElementById("age-gate");

  if (ageDialog) {
    const ageConfig = window.rhinegeistAgeGate || {};
    const ageConfirm = document.getElementById("ag-confirm");
    const root = document.documentElement;

    // The flag this replaces never expired, so it can't say when — or whether —
    // this visitor actually confirmed. Drop it rather than honour it.
    try {
      localStorage.removeItem("age-confirmed");
    } catch (_err) {
      // storage blocked; nothing to clean up
    }

    // Deliberately NOT showModal(). A modal dialog comes with the browser's
    // close watcher attached, and Escape closes it whether we like it or not:
    // the `cancel` event is only cancelable when the page already has user
    // activation, so on a fresh page load preventDefault() is ignored and the
    // visitor is straight in. A manual popover gets us the one thing showModal()
    // was actually worth — the top layer, which no z-index can paint over — with
    // no close watcher attached. The `popover` attribute is set here rather than
    // in the markup because it hides the element until showPopover() runs, which
    // would undo the whole point of shipping the gate open.
    const inertSiblings = (state) => {
      Array.prototype.forEach.call(document.body.children, (el) => {
        if (el !== ageDialog) {
          el.inert = state;
        }
      });
    };

    // UserWay and the consent banner both append to <body> well after this runs,
    // both at the very top of the z-index range. Keep anything that lands while
    // the gate is up inert too.
    const lateNodes = new MutationObserver((records) => {
      records.forEach((record) => {
        Array.prototype.forEach.call(record.addedNodes, (node) => {
          if (node.nodeType === 1 && node !== ageDialog) {
            node.inert = true;
          }
        });
      });
    });

    const raiseGate = () => {
      ageDialog.setAttribute("aria-modal", "true");
      inertSiblings(true);
      lateNodes.observe(document.body, { childList: true });

      if (typeof ageDialog.showPopover === "function") {
        try {
          ageDialog.setAttribute("popover", "manual");
          ageDialog.showPopover();
        } catch (_err) {
          // No top layer here; the stylesheet's z-index carries it instead.
          ageDialog.removeAttribute("popover");
        }
      }

      ageConfirm?.focus();
    };

    const unlockPage = () => {
      root.dataset.ageGate = "confirmed";
      ageDialog.removeAttribute("aria-modal");
      lateNodes.disconnect();
      inertSiblings(false);

      if (ageDialog.hasAttribute("popover")) {
        try {
          ageDialog.hidePopover();
        } catch (_err) {
          // already hidden
        }
        ageDialog.removeAttribute("popover");
      }

      if (ageDialog.open) {
        ageDialog.close();
      }
    };

    if (root.dataset.ageGate === "confirmed") {
      unlockPage();
    } else {
      raiseGate();
    }

    ageConfirm?.addEventListener("click", (event) => {
      event.preventDefault();
      unlockPage();

      if (pendingHash) {
        restorePendingHashEl();
        pinHashTarget(pendingHash);
      }

      // PHP sets the cookie rather than document.cookie: Safari caps anything
      // script writes at seven days, but not what arrives in a Set-Cookie
      // header. If the request can't be made, fall back to following the link,
      // which is the same confirmation as a plain navigation.
      if (!ageConfig.ajaxUrl) {
        return;
      }

      fetch(ageConfig.ajaxUrl, {
        method: "POST",
        credentials: "same-origin",
        // The gate closes immediately, so the visitor can click through to
        // another page while this is still in flight. keepalive lets it finish
        // anyway; without it the request is cancelled and they get re-gated.
        keepalive: true,
        body: new URLSearchParams({ action: "rhinegeist_confirm_age" }),
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(String(response.status));
          }
        })
        .catch(() => {
          window.location.href = ageConfirm.href;
        });
    });
  }
  // END 00 - AGE GATE
  // 01 - MOBILE MENU
  const mobMenuBtn = document.getElementById("mobile-menu-toggle");
  const mobMenu = document.getElementById("mobile-menu");
  const mediaQuery = window.matchMedia("(min-width: 900px)");
  function lockScroll() {
    document.documentElement.style.overflowY = "hidden";
  }
  function unlockScroll() {
    document.documentElement.style.overflowY = "";
  }

  // The closed panel is only hidden by `opacity: 0` and `pointer-events: none`,
  // neither of which takes anything out of the tab order. Every page therefore
  // shipped with the whole mobile menu — primary plus secondary, 23 links —
  // tabbable but invisible, at every width, including desktop where the panel
  // can't be opened at all. `inert` removes the subtree from both the tab order
  // and the accessibility tree without touching the slide animation, which
  // `visibility` would interrupt.
  // Both facts come from the same place, so the button can't advertise a state
  // the panel isn't in. The label stays "Menu" rather than flipping between
  // "open"/"close": aria-expanded already carries the state, and a label that
  // describes the action goes stale the moment the panel opens.
  const syncMobMenu = () => {
    const isOpen = mobMenu.classList.contains("open");
    mobMenu.inert = !isOpen;
    mobMenuBtn?.setAttribute("aria-expanded", isOpen ? "true" : "false");
  };

  const closeMobMenu = () => {
    mobMenu.classList.remove("open");
    mobMenu.classList.add("closed");
    unlockScroll();
    syncMobMenu();
  };

  if (!!mobMenuBtn && !!mobMenu) {
    syncMobMenu();

    mobMenuBtn.addEventListener("click", () => {
      if (mobMenu.classList.contains("open")) {
        mobMenu.classList.remove("open");
        mobMenu.classList.add("closed");
        unlockScroll();
      } else {
        mobMenu.classList.add("open");
        mobMenu.classList.remove("closed");
        lockScroll();
      }
      syncMobMenu();
    });

    // Escape closes it from anywhere inside, and focus goes back to the button
    // that opened it rather than being dropped at the top of the document.
    document.addEventListener("keydown", (event) => {
      if (event.key !== "Escape" || !mobMenu.classList.contains("open")) {
        return;
      }
      closeMobMenu();
      mobMenuBtn.focus();
    });

    mediaQuery.addEventListener("change", (e) => {
      if (e.matches) {
        mobMenu.classList.remove("open");
        mobMenu.classList.remove("closed");
        syncMobMenu();
        unlockScroll();
      }
    });
  }

  // Open/Close Submenu
  //
  // Each dropdown parent is a disclosure. The panel can be opened three ways —
  // `:hover`, `:focus-within` and this `.open` class (see _header.scss) — so
  // `aria-expanded` is recomputed from what is actually on screen rather than
  // tracked as its own flag, which would drift the moment a pointer user
  // hovered. The previous handler sat on the <li> and toggled on any click
  // inside it, so clicking a link *in* an open panel closed its own parent on
  // the way out.
  const lisWithSub = document.querySelectorAll(".menu-item-has-children");
  lisWithSub.forEach((li) => {
    const trigger = li.querySelector(":scope > a");
    const submenu = li.querySelector(":scope > ul.sub-menu");
    if (!trigger || !submenu) return;

    // The caller can override either input because the DOM lies at exactly the
    // moment these events fire: during focusout `document.activeElement` is
    // still <body> and the incoming element is only in `relatedTarget`, and
    // during mouseleave the element can still match `:hover`. Reading the live
    // value in those two cases leaves aria-expanded one step behind.
    const expanded = ({ focused, hovering } = {}) =>
      li.classList.contains("open") ||
      (focused === undefined ? li.contains(document.activeElement) : focused) ||
      (hovering === undefined ? li.matches(":hover") : hovering);

    const syncExpanded = (state) =>
      trigger.setAttribute("aria-expanded", expanded(state) ? "true" : "false");

    const setOpen = (open) => {
      li.classList.toggle("open", open);
      syncExpanded();
    };

    trigger.setAttribute("aria-expanded", "false");

    // These parents are Custom Links pointing at "#". Following that throws
    // the page to the top and opens nothing, so the click becomes the toggle
    // instead. A parent given a real URL in the menu editor keeps working as a
    // link — the keyboard still reaches its panel through :focus-within.
    const href = trigger.getAttribute("href") || "";
    if (href === "" || href === "#") {
      trigger.addEventListener("click", (event) => {
        event.preventDefault();
        setOpen(!li.classList.contains("open"));
      });
    }

    // :hover and :focus-within open the panel without going through setOpen,
    // so mirror them onto aria-expanded.
    li.addEventListener("mouseenter", () => syncExpanded({ hovering: true }));
    li.addEventListener("mouseleave", () => syncExpanded({ hovering: false }));
    li.addEventListener("focusin", () => syncExpanded({ focused: true }));
    li.addEventListener("focusout", (event) =>
      syncExpanded({ focused: li.contains(event.relatedTarget) }),
    );

    li.addEventListener("keydown", (event) => {
      if (event.key !== "Escape" || !expanded()) return;
      setOpen(false);
      trigger.focus();
    });
  });

  // A panel opened by click stays open until something closes it.
  document.addEventListener("click", (event) => {
    lisWithSub.forEach((li) => {
      if (li.classList.contains("open") && !li.contains(event.target)) {
        li.classList.remove("open");
        li.querySelector(":scope > a")?.setAttribute("aria-expanded", "false");
      }
    });
  });
  // END 01 - MOBILE MENU

  // 02 - MENU DROPDOWN
  const subMenus = document.querySelectorAll(".sub-menu");
  const primaryNav = document.getElementById("primary-nav");
  const pnSubMenus = primaryNav.querySelectorAll(".sub-menu");

  subMenus.forEach((sm) => {
    const liCount = sm.children.length;
    if (liCount < 4) {
      const dif = 4 - liCount;

      Array.from({ length: dif }, (_, i) => {
        const fill = document.createElement("li");
        fill.classList.add("fill");

        sm.appendChild(fill);
      });
    }

    // Add Featured Image
    // Decorative: the panel's links already say where they go, so an alt here
    // would just be noise read before every dropdown. Without the attribute
    // entirely, screen readers fall back to announcing the file name.
    const featImg = document.createElement("img");
    featImg.alt = "";
    // Root-relative, not the old primegeist host — that URL 301s, and via http
    // first, so the menu art cost a redirect chain on every page.
    featImg.src = "/wp-content/uploads/2026/06/menu-bg_01.png";
    featImg.classList.add("sub-menu__feat-img");
    sm.appendChild(featImg);
  });

  const pnDataAttrs = primaryNav.dataset;
  const definedImgUrls = Object.keys(pnDataAttrs).reduce((a, c) => {
    if (c.includes("ddfeatimg")) {
      return [...a, pnDataAttrs[c]];
    }
    return a;
  }, []);

  pnSubMenus.forEach((pnsm, pni) => {
    const pnFeatImg = pnsm.querySelector(".sub-menu__feat-img");
    if (pni <= definedImgUrls.length - 1) {
      pnFeatImg.src = definedImgUrls[pni];
    }
  });
  // END 02 - MENU DROPDOWN

  // 03 - INIT GLIDES
  //
  // None of these autoplay. Now Pouring, the event-space galleries and the
  // hero slides each advanced every 3s, which fails WCAG 2.2.2 — content that
  // moves on its own for more than five seconds needs a way to stop it, and
  // Glide's `hoverPause` is not one: it does nothing for a keyboard or a
  // touchscreen, and only one of the three even set it. Rather than add a
  // pause control to the eight-plus instances on /private-event-spaces/ alone,
  // the motion is gone. The arrows and swipe still work, and both jobs these
  // carousels do — reading 22 beer names, sizing up an event space — are
  // better served at the visitor's own pace.
  const ctaGlides = document.querySelectorAll(".glide--cta");
  const ctaListGlides = document.querySelectorAll(".glide--cta-list");
  const npGlides = document.querySelectorAll(".glide--np");
  const esGlides = document.querySelectorAll(".glide--es");

  // Mark the outgoing slide as --leaving on run.before so it starts fading
  // out immediately, concurrent with Glide's move, instead of waiting for
  // Glide to swap --active on run.after (see .glide__slide--leaving in _glide.scss)
  const bindSlideLeaveFade = (el, glideInstance) => {
    glideInstance.on("run.before", () => {
      el.querySelector(".glide__slide--active")?.classList.add(
        "glide__slide--leaving",
      );
    });
    glideInstance.on("run.after", () => {
      el.querySelectorAll(".glide__slide--leaving").forEach((slide) =>
        slide.classList.remove("glide__slide--leaving"),
      );
    });
  };

  // `type: "carousel"` clones the leading and trailing slides so the track can
  // wrap without a visible jump. The clones are ordinary DOM — links, headings
  // and all — so they joined the tab order as duplicates and screen readers
  // read every teaser twice: /taproom/ ships 22 Now Pouring slides and 12
  // clones, which was 34 tab stops for 22 beers. `inert` removes them from the
  // tab order and the accessibility tree while leaving them on screen.
  // Rebound on update because Glide rebuilds the clones when it re-runs
  // breakpoint config.
  const bindCloneInert = (el, glideInstance) => {
    const hideClones = () =>
      el
        .querySelectorAll(".glide__slide--clone")
        .forEach((clone) => (clone.inert = true));

    glideInstance.on(["mount.after", "update"], hideClones);
  };

  ctaGlides.forEach((glide) => {
    if (!glide.querySelector(".glide__slide")) return;
    const ctaGlideInstance = new Glide(glide, {
      type: "carousel",
      perView: 2.4,
      gap: 28,
      breakpoints: {
        1200: {
          perView: 2,
        },
        900: {
          perView: 1.2,
        },
      },
    });
    bindSlideLeaveFade(glide, ctaGlideInstance);
    bindCloneInert(glide, ctaGlideInstance);
    ctaGlideInstance.mount();
  });

  ctaListGlides.forEach((glide) => {
    if (!glide.querySelector(".glide__slide")) return;
    const ctaListGlideInstance = new Glide(glide, {
      type: "carousel",
      perView: 2.4,
      gap: 28,
      breakpoints: {
        1200: {
          perView: 2,
        },
        900: {
          perView: 1.2,
        },
      },
    });
    bindSlideLeaveFade(glide, ctaListGlideInstance);
    bindCloneInert(glide, ctaListGlideInstance);
    ctaListGlideInstance.mount();
  });

  npGlides.forEach((glide) => {
    if (!glide.querySelector(".glide__slide")) return;
    const npGlideInstance = new Glide(glide, {
      type: "carousel",
      perView: 4.5,
      breakpoints: {
        900: {
          perView: 1.45,
          gap: 0,
        },
      },
    });
    bindSlideLeaveFade(glide, npGlideInstance);
    bindCloneInert(glide, npGlideInstance);
    npGlideInstance.mount();
  });

  esGlides.forEach((glide) => {
    if (!glide.querySelector(".glide__slide")) return;
    const esGlideInstance = new Glide(glide, {
      type: "carousel",
      perView: 1,
    });
    bindSlideLeaveFade(glide, esGlideInstance);
    bindCloneInert(glide, esGlideInstance);
    esGlideInstance.mount();
  });

  // Related beers horizontal slider — mobile only; desktop uses the CSS grid
  const RELATED_BEERS_BP = 1200;
  const relatedBeersGlides = document.querySelectorAll(".glide--related-beers");
  relatedBeersGlides.forEach((glide) => {
    if (!glide.querySelector(".glide__slide")) return;

    let instance = null;

    const mountRelatedBeers = () => {
      if (instance) return;
      instance = new Glide(glide, {
        type: "carousel",
        perView: 2,
        gap: 24,
        breakpoints: {
          599: { perView: 1.2 },
        },
      });
      bindCloneInert(glide, instance);
      instance.mount();
    };

    const destroyRelatedBeers = () => {
      if (!instance) return;
      instance.destroy();
      instance = null;
    };

    const updateRelatedBeers = () => {
      if (window.innerWidth < RELATED_BEERS_BP) {
        mountRelatedBeers();
      } else {
        destroyRelatedBeers();
      }
    };

    updateRelatedBeers();

    let relatedBeersResizeTimer;
    window.addEventListener("resize", () => {
      clearTimeout(relatedBeersResizeTimer);
      relatedBeersResizeTimer = setTimeout(updateRelatedBeers, 150);
    });
  });

  if (pendingHash && !ageDialog?.open) {
    restorePendingHashEl();
    pinHashTarget(pendingHash);
  }

  window.addEventListener("hashchange", () => {
    if (window.location.hash) pinHashTarget(window.location.hash);
  });
  // END 03 - INIT GLIDES

  // 04 - POSTS LISTING FILTER
  const postsListings = document.querySelectorAll("[data-posts-listing]");

  if (postsListings.length && window.rhinegeistPostsFilter) {
    const { ajaxUrl, nonce } = window.rhinegeistPostsFilter;

    const setActiveFilter = (nav, categoryId) => {
      nav.querySelectorAll(".post-archive__filter").forEach((button) => {
        const isActive =
          String(button.dataset.categoryId || "0") === String(categoryId);
        button.classList.toggle("post-archive__filter--active", isActive);
        button.setAttribute("aria-pressed", isActive ? "true" : "false");
      });
    };

    const updateCategoryUrl = (slug) => {
      const url = new URL(window.location.href);
      if (slug) {
        url.searchParams.set("category", slug);
      } else {
        url.searchParams.delete("category");
      }
      url.searchParams.delete("page");
      url.searchParams.delete("paged");
      window.history.replaceState({}, "", url);
    };

    const fetchPosts = async (listing, { categoryId = 0, paged = 1 } = {}) => {
      const nav = listing.querySelector("[data-posts-filter-nav]");
      const results = listing.querySelector("[data-posts-listing-results]");
      if (!nav || !results) {
        return;
      }

      nav.classList.add("is-loading");
      results.classList.add("is-loading");

      const body = new FormData();
      body.append("action", "rhinegeist_filter_posts");
      body.append("nonce", nonce);
      body.append("category", String(categoryId));
      body.append("paged", String(paged));
      body.append("posts_per_page", listing.dataset.postsPerPage || "10");
      if (listing.dataset.categoryLimit) {
        body.append("category_limit", listing.dataset.categoryLimit);
      }

      try {
        const response = await fetch(ajaxUrl, {
          method: "POST",
          body,
          credentials: "same-origin",
        });
        const payload = await response.json();
        if (!payload.success) {
          return;
        }

        results.outerHTML = payload.data.html;
        setActiveFilter(nav, categoryId);

        const activeButton = nav.querySelector(
          `[data-category-id="${categoryId}"]`,
        );
        updateCategoryUrl(activeButton?.dataset.categorySlug || "");

        const newResults = listing.querySelector(
          "[data-posts-listing-results]",
        );
        newResults?.scrollIntoView({
          behavior: motionSafe("smooth"),
          block: "start",
        });
      } finally {
        nav.classList.remove("is-loading");
        listing
          .querySelector("[data-posts-listing-results]")
          ?.classList.remove("is-loading");
      }
    };

    postsListings.forEach((listing) => {
      const nav = listing.querySelector("[data-posts-filter-nav]");
      if (!nav) {
        return;
      }

      nav.addEventListener("click", (event) => {
        const button = event.target.closest(".post-archive__filter");
        if (!button || !nav.contains(button)) {
          return;
        }

        event.preventDefault();
        fetchPosts(listing, {
          categoryId: button.dataset.categoryId || "0",
          paged: 1,
        });
      });

      listing.addEventListener("click", (event) => {
        const link = event.target.closest(".pagination a");
        if (!link || !listing.contains(link)) {
          return;
        }

        event.preventDefault();
        const url = new URL(link.href, window.location.origin);
        const pathMatch = url.pathname.match(/\/page\/(\d+)/);
        const paged =
          Number(
            pathMatch?.[1] ||
              url.searchParams.get("page") ||
              url.searchParams.get("paged"),
          ) || 1;
        const activeButton = nav.querySelector(".post-archive__filter--active");
        fetchPosts(listing, {
          categoryId: activeButton?.dataset.categoryId || "0",
          paged,
        });
      });

      const initialCategory = new URL(window.location.href).searchParams.get(
        "category",
      );
      if (initialCategory) {
        const initialButton = nav.querySelector(
          `[data-category-slug="${initialCategory}"]`,
        );
        if (initialButton) {
          fetchPosts(listing, {
            categoryId: initialButton.dataset.categoryId || "0",
            paged: 1,
          });
        }
      }
    });
  }
  // END 04 - POSTS LISTING FILTER
  // 05 - STICKY NAV
  const pageSections = document.querySelectorAll("section");
  const stickyNav = document.querySelector("section.sticky-nav__wrapper");

  if (!!stickyNav) {
    const stickyList = stickyNav.querySelector("ul");
    if (!!stickyList) {
      pageSections.forEach((sect) => {
        if ("stickyNavLabel" in sect.dataset) {
          const newLi = document.createElement("li");
          const newLink = document.createElement("a");
          newLink.href = `#${sect.id}`;
          newLink.textContent = sect.dataset["stickyNavLabel"];
          newLi.appendChild(newLink);
          stickyList.appendChild(newLi);
        }
      });

      stickyList.addEventListener("click", (event) => {
        const link = event.target.closest("a[href^='#']");
        if (!link || !stickyList.contains(link)) return;

        const href = link.getAttribute("href") || "";
        const id = decodeURIComponent(href.slice(1));
        if (!document.getElementById(id)) return;

        event.preventDefault();
        history.pushState(null, "", href);
        scrollToHashTarget(href, "smooth");
      });
    }
  }
  // END 05 - STICKY NAV

  // 06 - BEER EXPLORE FILTER
  const beerExplore = document.querySelector("[data-beer-explore]");

  if (beerExplore) {
    const filterToggle = beerExplore.querySelector(
      "[data-beer-explore-filter-toggle]",
    );
    const sidebar = beerExplore.querySelector("[data-beer-explore-sidebar]");

    if (filterToggle && sidebar) {
      filterToggle.addEventListener("click", () => {
        const isOpen = sidebar.classList.toggle("open");
        filterToggle.setAttribute("aria-expanded", isOpen ? "true" : "false");
      });
    }

    const searchInput = beerExplore.querySelector("[data-beer-explore-search]");
    const countEl = beerExplore.querySelector("[data-beer-explore-count]");
    const headingEl = beerExplore.querySelector("[data-beer-explore-heading]");
    const emptyEl = beerExplore.querySelector("[data-beer-explore-empty]");
    const sentinel = beerExplore.querySelector("[data-beer-explore-sentinel]");
    const cards = Array.from(beerExplore.querySelectorAll("[data-beer-card]"));
    const filterBtns = beerExplore.querySelectorAll("[data-filter-type]");

    const BEER_EXPLORE_BATCH_SIZE = 24;
    let revealLimit = BEER_EXPLORE_BATCH_SIZE;

    // Each filter group holds an array of selected values. Selections within a
    // group are OR'd together; groups are AND'd against each other.
    const activeFilters = {
      availability: [],
      style: [],
      format: [],
      classification: [],
    };
    const filterParamKeys = Object.keys(activeFilters);

    const anyFilterActive = () =>
      filterParamKeys.some((type) => activeFilters[type].length);

    const findFilterBtn = (type, value) =>
      Array.from(filterBtns).find(
        (b) => b.dataset.filterType === type && b.dataset.filterValue === value,
      );

    const syncFiltersFromUrl = () => {
      const params = new URLSearchParams(window.location.search);

      filterParamKeys.forEach((type) => {
        const raw = params.get(type);
        if (!raw) return;

        raw.split(",").forEach((value) => {
          const btn = value && findFilterBtn(type, value);
          if (!btn || activeFilters[type].includes(value)) return;

          activeFilters[type].push(value);
          btn.setAttribute("aria-pressed", "true");
        });
      });
    };

    const syncUrlFromFilters = () => {
      const params = new URLSearchParams(window.location.search);

      filterParamKeys.forEach((type) => {
        if (activeFilters[type].length) {
          params.set(type, activeFilters[type].join(","));
        } else {
          params.delete(type);
        }
      });

      const query = params.toString();
      const newUrl = `${window.location.pathname}${query ? `?${query}` : ""}${
        window.location.hash
      }`;
      history.replaceState(null, "", newUrl);
    };

    const availabilityLabels = {
      year_round: "Year Round",
      seasonal: "Seasonal",
      limited: "Limited",
    };

    const updateResults = () => {
      const searchTerm = searchInput.value.toLowerCase().trim();
      const filtering = Boolean(searchTerm) || anyFilterActive();

      // Retired beers are no longer sold, so keep them out of the list until the
      // user explicitly opts in via the "Retired" availability filter.
      const retiredAllowed = activeFilters.availability.includes("retired");

      const matches = cards.filter((card) => {
        if (card.dataset.availability === "retired" && !retiredAllowed) {
          return false;
        }

        const nameMatch = !searchTerm || card.dataset.name.includes(searchTerm);
        const availMatch =
          !activeFilters.availability.length ||
          activeFilters.availability.includes(card.dataset.availability);
        const styleMatch =
          !activeFilters.style.length ||
          activeFilters.style.includes(card.dataset.style);
        const formatMatch =
          !activeFilters.format.length ||
          card.dataset.format
            .split(" ")
            .some((format) => activeFilters.format.includes(format));
        const classificationMatch =
          !activeFilters.classification.length ||
          activeFilters.classification.includes(card.dataset.classification);

        return (
          nameMatch &&
          availMatch &&
          styleMatch &&
          formatMatch &&
          classificationMatch
        );
      });

      // When no search/filter is active, only reveal beers in batches as the
      // user scrolls; an active search or filter shows every match at once.
      const limit = filtering ? matches.length : revealLimit;
      const visible = new Set(matches.slice(0, limit));

      cards.forEach((card) => {
        card.hidden = !visible.has(card);
      });

      const matchCount = matches.length;

      const activeAvailLabel =
        activeFilters.availability.length === 1 &&
        availabilityLabels[activeFilters.availability[0]];

      if (searchTerm) {
        countEl.textContent = `${matchCount} Result${
          matchCount !== 1 ? "s" : ""
        } for "${searchTerm}"`;
      } else if (activeAvailLabel) {
        countEl.textContent = `${matchCount} Result${
          matchCount !== 1 ? "s" : ""
        } for "${activeAvailLabel}"`;
      } else {
        countEl.textContent = `${matchCount} Beer${
          matchCount !== 1 ? "s" : ""
        }`;
      }

      if (headingEl) {
        headingEl.textContent =
          activeAvailLabel && !searchTerm
            ? activeAvailLabel
            : "Explore Our Beers";
      }

      emptyEl.hidden = matchCount > 0;

      if (sentinel) {
        sentinel.hidden = filtering || revealLimit >= matchCount;
      }
    };

    filterBtns.forEach((btn) => {
      btn.addEventListener("click", () => {
        const type = btn.dataset.filterType;
        const value = btn.dataset.filterValue;
        const selected = activeFilters[type];
        const idx = selected.indexOf(value);

        if (idx > -1) {
          selected.splice(idx, 1);
          btn.setAttribute("aria-pressed", "false");
        } else {
          selected.push(value);
          btn.setAttribute("aria-pressed", "true");
        }

        revealLimit = BEER_EXPLORE_BATCH_SIZE;
        updateResults();
        syncUrlFromFilters();
      });
    });

    searchInput.addEventListener("input", () => {
      revealLimit = BEER_EXPLORE_BATCH_SIZE;
      updateResults();
    });

    if (sentinel && "IntersectionObserver" in window) {
      const revealObserver = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            revealLimit += BEER_EXPLORE_BATCH_SIZE;
            updateResults();
          }
        },
        { rootMargin: "600px 0px" },
      );
      revealObserver.observe(sentinel);
    } else {
      // No scroll-based reveal available; show everything up front.
      revealLimit = cards.length;
    }

    syncFiltersFromUrl();
    updateResults();
  }
  // END 06 - BEER EXPLORE FILTER

  // 07 - EVENTS LISTING FILTER
  const eventsListing = document.querySelector("[data-events-listing]");

  if (eventsListing) {
    const filterBtns = eventsListing.querySelectorAll("[data-events-filter]");
    const cards = eventsListing.querySelectorAll("[data-event-card]");

    filterBtns.forEach((btn) => {
      btn.addEventListener("click", () => {
        const cat = btn.dataset.eventsFilter;

        filterBtns.forEach((b) => b.setAttribute("aria-pressed", "false"));
        btn.setAttribute("aria-pressed", "true");

        cards.forEach((card) => {
          const cats = card.dataset.eventCats || "";
          card.hidden = cat !== "all" && !cats.split(" ").includes(cat);
        });
      });
    });

    // Pre-select filter from URL, e.g. /events/category/taproom/
    const catMatch = window.location.pathname.match(/\/category\/([^\/]+)/);
    if (catMatch) {
      const slugFromUrl = catMatch[1];
      const matchBtn = eventsListing.querySelector(
        `[data-events-filter="${slugFromUrl}"]`,
      );
      if (matchBtn) matchBtn.click();
    }
  }
  // END 07 - EVENTS LISTING FILTER

  // 08 - WTB FEAT IMG SLIDE-IN RESIZE RESET
  // CSS scroll-driven animations auto-recalculate on resize, but the browser
  // can cache the animation state and not repaint. Resetting animation-name
  // forces a fresh evaluation against the new viewport dimensions.
  const wtbFeatTrack = document.querySelector(".wtb__feat-track");
  if (wtbFeatTrack) {
    let wtbResizeTimer;
    window.addEventListener("resize", () => {
      clearTimeout(wtbResizeTimer);
      wtbResizeTimer = setTimeout(() => {
        wtbFeatTrack.style.animationName = "none";
        wtbFeatTrack.offsetHeight; // force reflow
        wtbFeatTrack.style.animationName = "";
      }, 150);
    });
  }
  // END 08 - WTB FEAT IMG SLIDE-IN RESIZE RESET

  // 09 - TRIPLESEAT LEAD ATTRIBUTION
  //
  // Google Ads clicks land on a marketing/landing page with ?gclid=... (and
  // optionally utm_*), then the visitor clicks through to a page holding the
  // TripleSeat embed. Those params are left behind on the entry URL, so the
  // embed's hidden fields would be empty and every lead would look the same.
  // Here we (a) capture any click ID / UTM params seen on ANY page, then
  // (b) write them into TripleSeat's hidden fields wherever the embed appears
  // (the standalone form page and any landing page it's embedded on). Result:
  // a lead's gclid (= Google Ads paid; empty = organic/direct) and campaign are
  // recorded automatically, and the gclid is available for offline import.
  //
  // Storage: always sessionStorage (same-visit), plus a 90-day first-party
  // cookie for cross-visit attribution -- but the cookie is only written when
  // Termly "advertising" consent is granted, and removed if it's denied. The
  // 90-day window is anchored to the click time (not sliding), so repeat
  // visitors don't extend it indefinitely. 90 days covers the long private-
  // event/wedding shopping cycle (a March click can still attribute in June).
  //
  // Note: wbraid/gbraid (Google privacy-safe click IDs), fbclid (Meta) and
  // msclkid (Microsoft) have no TripleSeat field, so they are captured in the
  // stored record for the audit trail but not written to the embed. Those
  // clicks are still identified in TripleSeat via their UTMs.
  (() => {
    const STORE_KEY = "rgAttribution";
    const WINDOW_MS = 90 * 24 * 60 * 60 * 1000; // 90 days

    // URL param -> TripleSeat hidden field id
    const FIELD_MAP = {
      gclid: "lead_gclid",
      utm_source: "lead_campaign_source",
      utm_medium: "lead_campaign_medium",
      utm_campaign: "lead_campaign_name",
      utm_term: "lead_campaign_term",
      utm_content: "lead_campaign_content",
    };
    const PARAM_KEYS = Object.keys(FIELD_MAP);
    // Click IDs we capture but can't map to an embed field (no TripleSeat slot).
    const EXTRA_KEYS = ["wbraid", "gbraid", "fbclid", "msclkid"];
    const CAPTURE_KEYS = [...PARAM_KEYS, ...EXTRA_KEYS];
    // hasMappable: is there anything to write into the embed's hidden fields?
    const hasMappable = (d) => PARAM_KEYS.some((k) => d[k]);
    // hasAny: is there any attribution at all worth persisting?
    const hasAny = (d) => CAPTURE_KEYS.some((k) => d[k]);

    // --- storage helpers -----------------------------------------------------
    const parse = (raw) => {
      try {
        return JSON.parse(raw) || {};
      } catch (_err) {
        return {};
      }
    };
    const readCookie = () => {
      const m = document.cookie.match(/(?:^|;\s*)rgAttribution=([^;]*)/);
      return m ? parse(decodeURIComponent(m[1])) : {};
    };
    const readSession = () => {
      try {
        return parse(sessionStorage.getItem(STORE_KEY));
      } catch (_err) {
        return {};
      }
    };
    // Drop attribution older than the window (measured from click time).
    const prune = (d) =>
      d && d._ts && Date.now() - d._ts > WINDOW_MS ? {} : d || {};
    // We write the SAME object to cookie and session together, so they should
    // agree. If they ever diverge, trust whichever carries the newer click
    // (_ts) rather than merging them key-by-key (merging is what let stale
    // values from one store bleed into the other).
    const getStored = () => {
      const c = prune(readCookie());
      const s = prune(readSession());
      if (!s._ts) return c;
      if (!c._ts) return s;
      return s._ts >= c._ts ? s : c;
    };

    const writeSession = (data) => {
      try {
        sessionStorage.setItem(STORE_KEY, JSON.stringify(data));
      } catch (_err) {
        // sessionStorage blocked (private mode etc.) - fail quietly
      }
    };
    const writeCookie = (data) => {
      // Remaining lifetime keeps expiry anchored to the click across rewrites.
      const remaining = data._ts
        ? Math.floor((WINDOW_MS - (Date.now() - data._ts)) / 1000)
        : Math.floor(WINDOW_MS / 1000);
      if (remaining <= 0) return deleteCookie();
      document.cookie =
        STORE_KEY +
        "=" +
        encodeURIComponent(JSON.stringify(data)) +
        "; Max-Age=" +
        remaining +
        "; Path=/; SameSite=Lax; Secure";
    };
    const deleteCookie = () => {
      document.cookie =
        STORE_KEY + "=; Max-Age=0; Path=/; SameSite=Lax; Secure";
    };

    // --- consent gate --------------------------------------------------------
    // Termly's "advertising" category governs the persistent cookie.
    // Returns true (granted), false (denied), or null (not yet resolved).
    const adConsent = () => {
      try {
        const s = window.Termly && window.Termly.getConsentState();
        if (s && typeof s.advertising === "boolean") return s.advertising;
      } catch (_err) {
        // Termly not ready
      }
      return null;
    };

    // Always keep session copy; persist the cookie only with ad consent, and
    // clear it on explicit denial. Unknown consent leaves the cookie as-is.
    const persist = (data) => {
      writeSession(data);
      const consent = adConsent();
      if (!hasAny(data)) {
        if (consent === true) deleteCookie();
        return;
      }
      if (consent === true) writeCookie(data);
      else if (consent === false) deleteCookie();
    };

    // --- (a) capture ---------------------------------------------------------
    // Attribution is atomic. A visit that carries ANY recognized param REPLACES
    // the whole stored record (and resets the 90-day clock), so a later visit's
    // missing params can't inherit a prior click's values -- e.g. a Facebook
    // visit must not keep a stale Google gclid/term. A visit with no params
    // leaves the stored record untouched, so a direct/organic hit never wipes a
    // prior paid touch.
    const prior = getStored();
    const params = new URLSearchParams(window.location.search);
    const incoming = {};
    CAPTURE_KEYS.forEach((key) => {
      const val = params.get(key);
      if (val) incoming[key] = val;
    });
    const stored = hasAny(incoming) ? { ...incoming, _ts: Date.now() } : prior;
    persist(stored);

    // Re-flush to the cookie when consent resolves/changes after load (e.g. the
    // visitor accepts the banner). Termly pushes these to the dataLayer.
    const onConsentChange = () => persist(getStored());
    window.dataLayer = window.dataLayer || [];
    const origPush = window.dataLayer.push.bind(window.dataLayer);
    window.dataLayer.push = function (...args) {
      const ret = origPush(...args);
      try {
        const e = args[0];
        if (
          e &&
          (e.event === "userPrefUpdate" || e.event === "Termly.consentSaveDone")
        ) {
          onConsentChange();
        }
      } catch (_err) {
        // never let our hook break the dataLayer
      }
      return ret;
    };
    // Fallbacks in case consent resolved before our hook was installed.
    setTimeout(onConsentChange, 1500);
    setTimeout(onConsentChange, 4000);

    // --- (b) populate the embed (any page it appears on) ---------------------
    // Nothing mappable -> leave the form untouched so organic/direct leads stay
    // empty (and skip watching for the embed). A record holding only a non-
    // mappable click ID (wbraid/fbclid/...) also has nothing to write here.
    if (!hasMappable(stored)) return;

    // Fill each hidden field from storage, but only when it's empty, so we
    // never clobber a value the embed already read from the current page's own
    // URL (the direct-to-form path).
    const populate = () => {
      Object.entries(FIELD_MAP).forEach(([param, fieldId]) => {
        const val = stored[param];
        if (!val) return;
        const field = document.getElementById(fieldId);
        if (field && !field.value) field.value = val;
      });
    };

    // The embed renders asynchronously, so its fields may not exist yet on
    // DOMContentLoaded. Try now, then watch briefly until they appear.
    const attempt = () => {
      if (!document.getElementById("lead_gclid")) return false;
      populate();
      return true;
    };
    if (!attempt()) {
      let attempts = 0;
      const poll = setInterval(() => {
        attempts += 1;
        if (attempt() || attempts >= 40) clearInterval(poll); // ~10s max
      }, 250);
    }
  })();
  // END 09 - TRIPLESEAT LEAD ATTRIBUTION

  // 10 - TRIPLESEAT EMBED CUSTOMIZATIONS (header text + success redirect)
  //
  // Runs on any page that hosts the TripleSeat embed (standalone booking page
  // and any landing page it's dropped onto).
  //   - Renames the first section header to "Tell Us About Your Event".
  //   - Sets TS.custom_success_callback so a successful submission redirects to
  //     the thank-you page. For EMBEDDED forms this callback is the only place
  //     post-submit behavior is controlled -- TripleSeat's hosted-form redirect
  //     setting does not apply -- and it's the hook for firing Google Ads / Meta
  //     conversions in a later step. Defining it here (theme-wide) replaces the
  //     per-page inline script that hardcoded the old, dead thank-you URL.
  //   - On a successful submit, stashes the lead's email + phone in
  //     sessionStorage so the thank-you page (section 11) can hand them to
  //     Google Ads enhanced conversions, since the fields are gone by then.
  (() => {
    // Only proceed where the embed script is present.
    if (!document.querySelector('script[src*="ts_script"]')) return;

    const THANK_YOU =
      "https://rhinegeist.com/private-event-spaces/request-received-thanks/";
    const HEADER_TEXT = "Share Your Event Details";
    const ENH_KEY = "rgEnhConv";

    // Enhanced conversions feed Google ADS, so they follow the same Termly
    // "advertising" consent as the attribution cookie: capture unless the
    // visitor has explicitly opted out. Unknown/granted both capture (US is
    // opt-out; Google Consent Mode is the backstop). Send values raw -- Google's
    // tag hashes client-side, so we must not hash here.
    const adDenied = () => {
      try {
        const s = window.Termly && window.Termly.getConsentState();
        if (s && typeof s.advertising === "boolean")
          return s.advertising === false;
      } catch (_err) {
        // Termly not ready -> treat as not denied
      }
      return false;
    };
    // Best-effort E.164 for US numbers; leave anything we can't be sure of as-is.
    const toE164 = (raw) => {
      const trimmed = (raw || "").trim();
      if (!trimmed) return "";
      if (trimmed[0] === "+") return trimmed;
      const digits = trimmed.replace(/\D/g, "");
      if (digits.length === 10) return "+1" + digits;
      if (digits.length === 11 && digits[0] === "1") return "+" + digits;
      return trimmed;
    };
    const captureEnhConv = () => {
      if (adDenied()) return;
      const email =
        (document.getElementById("lead_email_address") || {}).value || "";
      const phone =
        (document.getElementById("lead_phone_number") || {}).value || "";
      if (!email && !phone) return;
      try {
        sessionStorage.setItem(
          ENH_KEY,
          JSON.stringify({ email: email.trim(), phone: toE164(phone) }),
        );
      } catch (_err) {
        // sessionStorage blocked -> enhanced conversions simply won't fire
      }
    };

    let headerDone = false;
    let callbackDone = false;

    const tick = () => {
      if (!headerDone) {
        const h = document.querySelector(
          "#tripleseat_embed_form_inline h2.row_contact_header",
        );
        if (h) {
          h.textContent = HEADER_TEXT;
          headerDone = true;
        }
      }
      // Wait for the form to render (fields present) so TS is fully initialised
      // before we attach the callback, and so we win over any inline script.
      if (
        !callbackDone &&
        window.TS &&
        document.getElementById("lead_first_name")
      ) {
        window.TS.custom_success_callback = function () {
          // Grab email/phone while the form still exists, then redirect.
          captureEnhConv();
          window.location.href = THANK_YOU;
        };
        callbackDone = true;
      }
      return headerDone && callbackDone;
    };

    if (!tick()) {
      let attempts = 0;
      const poll = setInterval(() => {
        attempts += 1;
        if (tick() || attempts >= 40) clearInterval(poll); // ~10s max
      }, 250);
    }
  })();
  // END 10 - TRIPLESEAT EMBED CUSTOMIZATIONS

  // 11 - ENHANCED CONVERSIONS: THANK-YOU DATALAYER PUSH
  //
  // The Google Ads conversion tag fires via GTM on the thank-you page (Window
  // Loaded trigger). Enhanced conversions need the lead's email + phone at that
  // moment, but the user has left the form page, so section 10 stashed them in
  // sessionStorage on submit. Here we push them to the dataLayer under the keys
  // the GTM User-Provided Data variable will read, then clear them so they can't
  // leak into a later, unrelated conversion.
  //
  // Timing: this runs at DOMContentLoaded, which always precedes window load, so
  // the push lands before the Window Loaded trigger fires. Values are raw --
  // Google's tag hashes client-side.
  (() => {
    if (window.location.pathname.indexOf("/request-received-thanks") === -1) {
      return;
    }
    const ENH_KEY = "rgEnhConv";
    let data;
    try {
      data = JSON.parse(sessionStorage.getItem(ENH_KEY) || "null");
    } catch (_err) {
      data = null;
    }
    if (!data || (!data.email && !data.phone)) return;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      enh_conv_email: data.email || "",
      enh_conv_phone: data.phone || "",
    });

    try {
      sessionStorage.removeItem(ENH_KEY);
    } catch (_err) {
      // nothing to do
    }
  })();
  // END 11 - ENHANCED CONVERSIONS: THANK-YOU DATALAYER PUSH

  // 12 - SLIDESHOW HERO
  //
  // Unlike the Glide carousels (see 03), this one advances on its own, so it
  // carries what WCAG 2.2.2 asks for: a visible pause button. It also holds
  // still while keyboard focus is inside it, and starts paused for anyone with
  // reduced motion on. It deliberately doesn't pause on hover: the hero fills
  // the screen, so a resting cursor would stop it for most desktop visitors.
  // The live region stays off while it rotates so a screen reader isn't
  // interrupted every few seconds, and announces slide changes once the
  // visitor has paused it.
  document.querySelectorAll("[data-hero-slideshow]").forEach((el) => {
    const slidesWrap = el.querySelector(".hero-slideshow__slides");
    const slides = [...el.querySelectorAll(".hero-slideshow__slide")];
    const toggle = el.querySelector("[data-hero-toggle]");
    if (slides.length < 2 || !toggle) return;

    const interval = parseInt(el.dataset.interval, 10) || 6000;
    let current = 0;
    let timer = null;
    let userPaused = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    let focused = false;

    const show = (index) => {
      current = (index + slides.length) % slides.length;
      slides.forEach((slide, i) => {
        slide.classList.toggle("is-active", i === current);
        slide.inert = i !== current;
      });
    };

    const schedule = () => {
      clearTimeout(timer);
      if (userPaused || focused) return;
      timer = setTimeout(() => {
        show(current + 1);
        schedule();
      }, interval);
    };

    const syncToggle = () => {
      el.classList.toggle("is-paused", userPaused);
      toggle.setAttribute(
        "aria-label",
        userPaused ? "Play slideshow" : "Pause slideshow",
      );
      slidesWrap.setAttribute("aria-live", userPaused ? "polite" : "off");
    };

    toggle.addEventListener("click", () => {
      userPaused = !userPaused;
      syncToggle();
      schedule();
    });

    el.querySelector("[data-hero-prev]")?.addEventListener("click", () => {
      show(current - 1);
      schedule();
    });

    el.querySelector("[data-hero-next]")?.addEventListener("click", () => {
      show(current + 1);
      schedule();
    });

    // Swipe: a mostly-horizontal drag of 50px or more changes slide. Anything
    // more vertical than that is left alone so the page still scrolls, and a
    // drag that starts on a CTA never becomes a click, so no guard is needed.
    const SWIPE_MIN = 50;
    let touchX = null;
    let touchY = null;

    el.addEventListener(
      "touchstart",
      (e) => {
        if (e.touches.length !== 1) {
          touchX = null;
          return;
        }
        touchX = e.touches[0].clientX;
        touchY = e.touches[0].clientY;
      },
      { passive: true },
    );

    el.addEventListener(
      "touchend",
      (e) => {
        if (touchX === null) return;
        const dx = e.changedTouches[0].clientX - touchX;
        const dy = e.changedTouches[0].clientY - touchY;
        touchX = null;
        if (Math.abs(dx) < SWIPE_MIN || Math.abs(dx) < Math.abs(dy)) return;
        show(dx < 0 ? current + 1 : current - 1);
        schedule();
      },
      { passive: true },
    );

    el.addEventListener("touchcancel", () => (touchX = null), {
      passive: true,
    });

    el.addEventListener("focusin", () => {
      focused = true;
      schedule();
    });

    el.addEventListener("focusout", (e) => {
      if (el.contains(e.relatedTarget)) return;
      focused = false;
      schedule();
    });

    syncToggle();
    schedule();
  });
  // END 12 - SLIDESHOW HERO
});
